import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeSchema from "_shared/fee-schedules/categories/fees/fee-schema";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import { Currency } from "_shared/money/currencies/currency";
import { z } from "zod";

export type FeeScheduleCategoryJSON = z.infer<typeof FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ>;
export type FeeScheduleCategoryParams = z.infer<typeof FeeScheduleCategorySchema.FeeScheduleCategoryZ>;

export default class FeeScheduleCategorySchema {
  public static readonly FeeScheduleCategoryJSONZ = z.object({
    id: z.uuid().nullish(),
    name: z.string(),
    description: z.string().nullish(),
    billingCode: z.string().nullish(),
    currencyIsMasked: z.boolean().nullish(),
    feeRate: z.number().nullish(),
    feeCurrency: z.enum(Currency).nullish(),
    feeUnit: z.string().nullish(),
  });

  public static readonly FeeScheduleCategoryZ = z.object({
    id: z.instanceof(Guid),
    name: z.instanceof(FeeScheduleCategoryName),
    description: z.instanceof(FeeScheduleCategoryDescription).optional().nullable(),
    fee: z.instanceof(Fee).nullish(),
    billingCode: z.instanceof(FeeScheduleBillingCode).optional().nullable(),
    currencyIsMasked: z.boolean().optional(),
  });

  public static validate(values: unknown): FeeScheduleCategoryParams {
    return parseOrThrow(FeeScheduleCategorySchema.FeeScheduleCategoryZ, values, {
      logError: true,
      schemaName: "FeeScheduleCategorySchema",
      operation: "Validating FeeScheduleCategoryParams",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): FeeScheduleCategoryJSON {
    return parseOrThrow(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleCategorySchema",
      operation: "Validating FeeScheduleCategoryJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is FeeScheduleCategoryParams {
    try {
      FeeScheduleCategorySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is FeeScheduleCategoryJSON {
    try {
      FeeScheduleCategorySchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: FeeScheduleCategoryJSON): FeeScheduleCategoryParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ, json, {
      logError: true,
      schemaName: "FeeScheduleCategorySchema",
      operation: "Deserializing FeeScheduleCategoryJSON",
      payloadSample: json,
    });
    return FeeScheduleCategorySchema.codec.decode(parsed);
  }

  public static serialize(category: FeeScheduleCategoryParams): FeeScheduleCategoryJSON {
    if (isNullOrUndefined(category)) return category;
    const parsed = FeeScheduleCategorySchema.validate(category);
    if (isNullOrUndefined(parsed)) return parsed;
    return FeeScheduleCategorySchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ,
    FeeScheduleCategorySchema.FeeScheduleCategoryZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id) ?? Guid.generate(),
        name: new FeeScheduleCategoryName(data.name),
        description: data.description ? new FeeScheduleCategoryDescription(data.description) : undefined,
        fee:
          !isNullOrUndefined(data.feeRate) && data.feeCurrency
            ? Fee.create(
                FeeSchema.deserialize({
                  rate: {
                    amount: data.feeRate ?? 0,
                    currency: data.feeCurrency,
                  },
                  unit: data.feeUnit,
                }),
              )
            : undefined,
        billingCode: data.billingCode ? new FeeScheduleBillingCode(data.billingCode) : undefined,
        currencyIsMasked: data.currencyIsMasked ?? false,
      }),
      encode: (category) => ({
        id: category.id?.toJSON(),
        name: category.name.value,
        description: category.description?.value,
        feeRate: category.fee?.rate.amount ?? 0,
        feeCurrency: category.fee?.rate.currency,
        feeUnit: category.fee?.unit ?? undefined,
        billingCode: category.billingCode?.value,
        currencyIsMasked: category.currencyIsMasked,
      }),
    },
  );
}
